import { useState, useEffect, useRef } from 'react';
import { api } from '../api/client';
import type { RestrictionLevel, Tariff, TariffVersion, TariffBooklet, Municipality } from '../api/types';
import { Card, Badge, Alert, DataTable } from '../components/ui';
import type { DataTableColumn, BadgeTone } from '../components/ui';
import { chargeTypeLabel, unitForChargeType } from '../lib/chargeType';
import { LEVEL_LABELS, RESTRICTION_LEVELS, WATER_WISE_LABEL, isRestrictableChargeType } from '../lib/waterRestrictions';
import {
  classifyVersion,
  todayDateKey,
  versionEffectivityLabel,
} from '../lib/tariffEffectivity';
import type { VersionEffectivity } from '../lib/tariffEffectivity';
import {
  checkCandidateMesh,
  chainIntegrityDetailLabel,
  defaultNextEffectiveFrom,
  summariseChainIntegrity,
  toDateKey as chainToDateKey,
} from '../lib/tariffChainIntegrity';
// The rolled-up tariff-CARD signal (TV-4 + the 2026-08-06 pill redesign):
// one pill, reusing the card's existing Active/Inactive slot rather than a
// separate badge. Per-version rows stay on tariffEffectivity's own
// classify/label pair above — this is a DIFFERENT, coarser question.
import { tariffStatusPill, type TariffVersionRow } from '../lib/tariffStatusPill';
import { MunicipalityManager } from '../components/MunicipalityManager';

const CHARGE_TYPE_OPTIONS = [
  'energy_kwh', 'max_demand_kva', 'nmd_kva', 'fixed_monthly', 'service_charge', 'reactive_kvarh',
  'network_charge', 'levy', 'administration_charge', 'network_capacity_charge',
  'network_demand_charge', 'transmission_network_charge', 'urban_low_voltage_subsidy_charge',
  'excess_network_capacity_charge',
  'ancillary_service_charge', 'generator_capacity_charge', 'legacy_charge',
  'water_kl', 'sanitation_kl', 'sanitation_fixed',
] as const;

// sanitation_kl is priced on this % of the water volume. The DB refuses a
// sanitation_kl row without one in (0, 100], and a % on any other row.
const SANITATION_PERCENT_ERROR = 'Enter the % of water consumption (e.g. 95) under the rate on the Sanitation row. It must be more than 0 and at most 100.';
const isValidVolumePercent = (p: number | null): boolean => p !== null && p > 0 && p <= 100;

// ── Effectivity ───────────────────────────────────────────────────────────
// Per-VERSION row tone. The `null`-labelled states (`current`/`open`/
// `inactive`) are unreachable in render — their label function returns null
// and nothing is drawn — but they are listed so the map is total and no
// cast is needed under `noUncheckedIndexedAccess`.
//
// `danger` for expired is Beau's ask, verbatim: "perhaps also a red-pill or
// something on the tariff to indicate it has expired when it reaches its end
// date". Every tone here is an EXISTING ui/Badge tone, so this introduces no
// new colour pair — each one's fg/bg ratio and its dark fill against a Card
// are already asserted in src/test/contrast.test.ts.
const VERSION_EFFECTIVITY_TONE: Record<VersionEffectivity, BadgeTone> = {
  expired: 'danger',
  expiring: 'warning',
  scheduled: 'info',
  current: 'neutral',
  open: 'neutral',
  inactive: 'neutral',
};

/**
 * How many version fetches to keep in flight during the effectivity sweep.
 *
 * The tariff LIST endpoint returns `version_count` and `latest_fy` and no
 * dates at all (billing-api/src/index.ts:2397), so a list-level expiry signal
 * has to read each tariff's versions. That is the repo's standing shape for
 * this — "no bulk API endpoints — do bulk actions as N serial single-id calls"
 * — except serial across ~45 tariffs would cost several seconds, so this runs
 * a small pool instead. Production has 45 `tariff_version` rows in total, so
 * the fan-out is bounded and small.
 *
 * FOLLOW-UP for whoever next owns billing-api: adding the latest active
 * version's `effective_from`/`effective_to` to the `/api/v2/tariffs` SELECT
 * would delete this sweep entirely. It is a Worker change, out of scope here.
 */
const EFFECTIVITY_SWEEP_CONCURRENCY = 6;

const TOU_BUCKET_OPTIONS = ['peak', 'standard', 'off_peak', 'all'] as const;
const SEASON_OPTIONS = ['high', 'low', 'all'] as const;
const UNIT_OPTIONS = ['kwh', 'kWh', 'kVA', 'kVA/month', 'POD/day', 'kVarh', 'kL', 'day', 'month'] as const;

interface RateRow {
  charge_type: string;
  tou_bucket: string;
  season: string;
  rate_cents: number;
  unit: string;
  description: string;
  // Not edited here, but carried through so a save cannot null it: every save
  // used to, resetting reactive thresholds to the calculator's default 30%.
  threshold_value: number | null;
  volume_percent: number | null;
  // water_kl / sanitation_kl only: the restriction level this rate prices at;
  // null = Water Wise (spec 2026-10-08).
  restriction_level: RestrictionLevel | null;
}

const emptyRateRow = (): RateRow => ({
  charge_type: 'energy_kwh',
  tou_bucket: 'all',
  season: 'all',
  rate_cents: 0,
  unit: 'kWh',
  description: '',
  threshold_value: null,
  volume_percent: null,
  restriction_level: null,
});

// Moved to src/lib/chargeType.ts, shared with Tariffs.tsx. These labels carry
// the unit because they are shown as bare <select> options with nothing else
// on screen to say what the charge is measured in — the opposite context to a
// bill line item, which prints its own `unit` and must therefore use the
// unit-free `chargeTypeName`. Same table, two registers.

export default function TariffManagement() {
  const [tariffs, setTariffs] = useState<Tariff[]>([]);
  const [municipalities, setMunicipalities] = useState<Municipality[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [filterMunicipality, setFilterMunicipality] = useState<string>('');

  // Expanded tariff
  const [expandedTariffId, setExpandedTariffId] = useState<string | null>(null);
  const [versions, setVersions] = useState<TariffVersion[]>([]);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Modals
  const [showCreateTariff, setShowCreateTariff] = useState(false);
  const [showVersionModal, setShowVersionModal] = useState(false);
  const [editingVersion, setEditingVersion] = useState<TariffVersion | null>(null);
  const [editingTariffId, setEditingTariffId] = useState<string | null>(null);

  // Edit tariff modal
  const [showEditTariff, setShowEditTariff] = useState(false);
  // municipality_id: '' = None (global), matching the Create form's select.
  const [editTariffForm, setEditTariffForm] = useState({ name: '', description: '', category: '', municipality_id: '', restriction_exempt: false });
  // The tick when the dialog opened: like the municipality, it is sent only
  // when changed, so an untouched save reads exactly as before.
  const [editTariffOriginalExempt, setEditTariffOriginalExempt] = useState(false);
  const [editTariffId, setEditTariffId] = useState<string | null>(null);
  const [updatingTariff, setUpdatingTariff] = useState(false);
  // The tariff's municipality when the dialog opened, so only a CHANGE is
  // sent, plus its name in case it is missing from the loaded list.
  const [editTariffOriginalMunicipality, setEditTariffOriginalMunicipality] =
    useState<{ id: string; name: string | null }>({ id: '', name: null });
  // The API's own sentence from a 409 `holiday_pricing_change`. The rule
  // lives in billing-api (tariffs/municipality-change.ts); the dialog only
  // shows its answer and offers "Change anyway". Never re-derived here.
  const [holidayPricingWarning, setHolidayPricingWarning] = useState<string | null>(null);

  // Type-to-confirm delete (soft delete: sets is_active=0). Guards against
  // misclicks on similarly-named tariffs (e.g. Landrate 1 / Landrate 2).
  const [deleteTarget, setDeleteTarget] = useState<Tariff | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Create tariff form
  const [newTariff, setNewTariff] = useState({
    municipality_id: '',
    name: '',
    description: '',
    category: '',
    rate_type: 'simple' as 'simple' | 'tou',
    restriction_exempt: false,
  });
  const [creating, setCreating] = useState(false);

  // Version form
  const [versionForm, setVersionForm] = useState({
    financial_year: '',
    effective_from: '',
    effective_to: '',
    is_gazetted: false,
    gazetted_date: '',
  });
  const [rateRows, setRateRows] = useState<RateRow[]>([emptyRateRow()]);
  const [savingVersion, setSavingVersion] = useState(false);
  // Set by a refused save; marks each sanitation % box still invalid.
  const [showPercentErrors, setShowPercentErrors] = useState(false);

  // TV-4: explicit "save it anyway" for a gap the form's own pre-submit
  // check found — never clears an overlap. Reset whenever the modal opens
  // or the dates change, so an acknowledgment never silently carries over
  // to a DIFFERENT gap than the one it was given for.
  const [acknowledgeGap, setAcknowledgeGap] = useState(false);

  // Effectivity — every tariff's versions, for the list-level expiry signal.
  // Keyed by tariff id; a missing key means "not swept yet, or the fetch
  // failed", and renders no badge rather than a wrong one.
  const [versionsByTariff, setVersionsByTariff] = useState<Record<string, TariffVersion[]>>({});
  const sweepSeq = useRef(0);

  // Booklets
  const [booklets, setBooklets] = useState<TariffBooklet[]>([]);
  const [loadingBooklets, setLoadingBooklets] = useState(false);
  const [uploadingBooklet, setUploadingBooklet] = useState(false);
  const [bookletFY, setBookletFY] = useState('');
  const [bookletMunicipality, setBookletMunicipality] = useState('');

  useEffect(() => {
    loadMunicipalities();
  }, []);

  useEffect(() => {
    loadTariffs();
  }, [filterMunicipality]);

  const loadMunicipalities = async () => {
    try {
      const res = await api.getMunicipalities();
      setMunicipalities(res.data);
    } catch (err) {
      console.error('Failed to load municipalities:', err);
    }
  };

  const loadTariffs = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: { municipality_id?: string } = {};
      if (filterMunicipality) params.municipality_id = filterMunicipality;
      const res = await api.getTariffsV2(params);
      setTariffs(res.data);
      // Deliberately NOT awaited: the sweep is decoration on an already-usable
      // page, and blocking the skeleton on ~45 extra round-trips would trade a
      // real regression for a badge.
      void sweepEffectivity(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load tariffs');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Read every listed tariff's versions so the list can show which ones have
   * nothing in effect.
   *
   * This is the whole point of the list-level signal: 21 tariffs expired
   * 2026-06-30 and the only way to find them today is to open each card in
   * turn — which is the same "nobody noticed" failure, one click deeper.
   *
   * Two things about the shape:
   *  - `sweepSeq` discards a sweep whose results land after a newer one
   *    started (change the municipality filter fast enough and the old
   *    sweep's `await`s resolve into a list that is no longer on screen).
   *  - state is set ONCE at the end rather than per tariff, so the page does
   *    not re-render 45 times and badges do not trickle in one by one.
   *
   * A failed fetch is swallowed per tariff: that tariff simply keeps no entry
   * and shows no badge. Silence is the right degradation here because the
   * badge is additive — unlike the Create Bill Run guard, which fails CLOSED
   * because there the answer gates an action.
   */
  const sweepEffectivity = async (list: Tariff[]) => {
    const seq = ++sweepSeq.current;
    setVersionsByTariff({});
    if (list.length === 0) return;

    const queue = list.map((t) => t.id);
    const found: Record<string, TariffVersion[]> = {};
    const pool = Array.from(
      { length: Math.min(EFFECTIVITY_SWEEP_CONCURRENCY, queue.length) },
      async () => {
        for (;;) {
          const id = queue.shift();
          if (id === undefined) return;
          try {
            const res = await api.getTariffVersions(id);
            found[id] = res.data;
          } catch {
            // See above: no entry, no badge.
          }
        }
      },
    );

    await Promise.all(pool);
    if (seq === sweepSeq.current) setVersionsByTariff(found);
  };

  const loadVersions = async (tariffId: string) => {
    try {
      setLoadingVersions(true);
      const res = await api.getTariffVersions(tariffId);
      setVersions(res.data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load versions');
    } finally {
      setLoadingVersions(false);
    }
  };

  const loadBooklets = async (municipalityId: string, fy?: string) => {
    try {
      setLoadingBooklets(true);
      const params: { municipality_id?: string; financial_year?: string } = { municipality_id: municipalityId };
      if (fy) params.financial_year = fy;
      const res = await api.getBooklets(params);
      setBooklets(res.data);
    } catch (err) {
      console.error('Failed to load booklets:', err);
    } finally {
      setLoadingBooklets(false);
    }
  };

  const handleOpenEditTariff = (tariff: Tariff, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditTariffId(tariff.id);
    setEditTariffForm({
      name: tariff.name,
      description: tariff.description || '',
      category: tariff.category || '',
      municipality_id: tariff.municipality_id ?? '',
      restriction_exempt: tariff.restriction_exempt === 1,
    });
    setEditTariffOriginalExempt(tariff.restriction_exempt === 1);
    setEditTariffOriginalMunicipality({ id: tariff.municipality_id ?? '', name: tariff.municipality_name ?? null });
    setHolidayPricingWarning(null);
    setShowEditTariff(true);
  };

  const closeEditTariff = () => {
    setShowEditTariff(false);
    setHolidayPricingWarning(null);
  };

  const openDelete = (tariff: Tariff, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTarget(tariff);
    setDeleteConfirmText('');
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    if (deleteConfirmText.trim() !== deleteTarget.name) return;
    try {
      setDeleting(true);
      setError(null);
      // v2 endpoint — soft-deletes by setting is_active=0. Existing meter
      // assignments are preserved; the tariff just disappears from active
      // pickers.
      await api.deleteTariffV2(deleteTarget.id);
      setDeleteTarget(null);
      setDeleteConfirmText('');
      await loadTariffs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete tariff');
    } finally {
      setDeleting(false);
    }
  };

  const submitTariffUpdate = async (acknowledgeHolidayChange: boolean) => {
    if (!editTariffId) return;
    // Send the municipality only when the admin changed it, so an untouched
    // one never reaches the server's holiday-pricing check. '' is "None
    // (global)", which the API takes as null.
    const municipalityChanged = editTariffForm.municipality_id !== editTariffOriginalMunicipality.id;
    try {
      setUpdatingTariff(true);
      await api.updateTariffV2(editTariffId, {
        name: editTariffForm.name,
        description: editTariffForm.description,
        category: editTariffForm.category,
        ...(editTariffForm.restriction_exempt !== editTariffOriginalExempt ? { restriction_exempt: editTariffForm.restriction_exempt } : {}),
        ...(municipalityChanged ? { municipality_id: editTariffForm.municipality_id || null } : {}),
        ...(acknowledgeHolidayChange ? { acknowledge_holiday_change: true } : {}),
      });
      closeEditTariff();
      await loadTariffs();
    } catch (err) {
      // client.ts request() attaches the HTTP status and parsed body.
      const status = (err as { status?: number }).status;
      const code = (err as { body?: { code?: unknown } }).body?.code;
      if (status === 409 && code === 'holiday_pricing_change') {
        setHolidayPricingWarning(err instanceof Error ? err.message : 'This change alters holiday pricing.');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to update tariff');
      }
    } finally {
      setUpdatingTariff(false);
    }
  };

  const handleUpdateTariff = async (e: React.FormEvent) => {
    e.preventDefault();
    await submitTariffUpdate(false);
  };

  const handleExpandTariff = async (tariffId: string) => {
    if (expandedTariffId === tariffId) {
      setExpandedTariffId(null);
      setVersions([]);
      return;
    }
    setExpandedTariffId(tariffId);
    await loadVersions(tariffId);
  };

  const handleCreateTariff = async () => {
    if (!newTariff.name.trim()) {
      setError('Tariff name is required');
      return;
    }
    try {
      setCreating(true);
      setError(null);
      await api.createTariffV2({
        municipality_id: newTariff.municipality_id || undefined,
        name: newTariff.name,
        description: newTariff.description || undefined,
        category: newTariff.category || undefined,
        rate_type: newTariff.rate_type,
        restriction_exempt: newTariff.restriction_exempt,
      });
      setShowCreateTariff(false);
      setNewTariff({ municipality_id: '', name: '', description: '', category: '', rate_type: 'simple', restriction_exempt: false });
      await loadTariffs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create tariff');
    } finally {
      setCreating(false);
    }
  };

  const openVersionModal = (tariffId: string, version?: TariffVersion) => {
    setEditingTariffId(tariffId);
    if (version) {
      setEditingVersion(version);
      setVersionForm({
        financial_year: version.financial_year,
        effective_from: version.effective_from.split('T')[0],
        effective_to: version.effective_to ? version.effective_to.split('T')[0] : '',
        is_gazetted: !!version.is_gazetted,
        gazetted_date: version.gazetted_date ? version.gazetted_date.split('T')[0] : '',
      });
      // Load version detail with rates
      loadVersionDetail(tariffId, version.id);
    } else {
      setEditingVersion(null);
      // TV-4: default effective_from to the current chain's own end + 1 day,
      // so meshing is what happens when nobody thinks about it (design spec
      // §5.4). `versions` is already this tariff's own list — "Add Version"
      // only renders once its tariff is expanded, which is what loaded it.
      // `null` (no active bounded version to continue from — the first
      // version ever, or an already open-ended chain) leaves it blank, same
      // as before this change.
      const defaultFrom = defaultNextEffectiveFrom(versions);
      setVersionForm({
        financial_year: '',
        effective_from: defaultFrom ?? '',
        effective_to: '',
        is_gazetted: false,
        gazetted_date: '',
      });
      setRateRows([emptyRateRow()]);
    }
    setAcknowledgeGap(false);
    setShowPercentErrors(false);
    setShowVersionModal(true);
  };

  const loadVersionDetail = async (tariffId: string, versionId: string) => {
    try {
      const res = await api.getTariffVersion(tariffId, versionId);
      const v = res.data;
      if (v.rates && v.rates.length > 0) {
        setRateRows(v.rates.map(r => ({
          charge_type: r.charge_type,
          tou_bucket: r.tou_bucket || 'all',
          season: r.season || 'all',
          rate_cents: r.rate_cents,
          unit: r.unit,
          description: r.description || '',
          threshold_value: r.threshold_value ?? null,
          volume_percent: r.volume_percent ?? null,
          restriction_level: r.restriction_level ?? null,
        })));
      } else {
        setRateRows([emptyRateRow()]);
      }
    } catch (err) {
      console.error('Failed to load version detail:', err);
      setRateRows([emptyRateRow()]);
    }
  };

  const handleSaveVersion = async () => {
    if (!editingTariffId || !versionForm.financial_year || !versionForm.effective_from) {
      setError('Financial year and effective from date are required');
      return;
    }
    if (rateRows.some(r => r.charge_type === 'sanitation_kl' && !isValidVolumePercent(r.volume_percent))) {
      setShowPercentErrors(true);
      setError(SANITATION_PERCENT_ERROR);
      return;
    }
    try {
      setSavingVersion(true);
      setError(null);
      const data = {
        financial_year: versionForm.financial_year,
        effective_from: versionForm.effective_from,
        effective_to: versionForm.effective_to || undefined,
        is_gazetted: versionForm.is_gazetted,
        gazetted_date: versionForm.is_gazetted && versionForm.gazetted_date ? versionForm.gazetted_date : undefined,
        // TV-4: explicit "save it anyway" for a gap the form's own check
        // found (see the Alert below the date inputs). Never rescues an
        // overlap — the server rejects that unconditionally regardless of
        // this flag.
        acknowledge_gap: acknowledgeGap,
        rates: rateRows.map(r => ({
          charge_type: r.charge_type,
          tou_bucket: r.tou_bucket !== 'all' ? r.tou_bucket : undefined,
          season: r.season !== 'all' ? r.season : undefined,
          rate_cents: r.rate_cents,
          unit: r.unit,
          description: r.description || undefined,
          threshold_value: r.threshold_value ?? undefined,
          // Only sanitation_kl carries a %: a row switched away keeps it in
          // state (switching back restores it) but never sends it.
          volume_percent: r.charge_type === 'sanitation_kl' && r.volume_percent !== null ? r.volume_percent : undefined,
          // Only per-kL water and sanitation carry a level; a row switched
          // away keeps it in state but never sends it (as volume_percent).
          restriction_level: isRestrictableChargeType(r.charge_type) && r.restriction_level ? r.restriction_level : undefined,
        })),
      };

      if (editingVersion) {
        await api.updateTariffVersion(editingTariffId, editingVersion.id, data);
      } else {
        await api.createTariffVersion(editingTariffId, data);
      }

      setShowVersionModal(false);
      setEditingVersion(null);
      setEditingTariffId(null);
      await loadVersions(expandedTariffId!);
      await loadTariffs();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save version');
    } finally {
      setSavingVersion(false);
    }
  };

  const addRateRow = () => {
    setRateRows([...rateRows, emptyRateRow()]);
  };

  const removeRateRow = (index: number) => {
    if (rateRows.length <= 1) return;
    setRateRows(rateRows.filter((_, i) => i !== index));
  };

  const updateRateRow = (index: number, field: keyof RateRow, value: string | number | null) => {
    const updated = [...rateRows];
    updated[index] = { ...updated[index], [field]: value };
    if (field === 'charge_type' && typeof value === 'string') {
      updated[index].unit = unitForChargeType(value, updated[index].unit);
    }
    setRateRows(updated);
  };

  const handleUploadBooklet = async (file: File) => {
    if (!bookletMunicipality || !bookletFY) {
      setError('Select a municipality and financial year for the booklet');
      return;
    }
    try {
      setUploadingBooklet(true);
      setError(null);
      await api.uploadBooklet(file, bookletMunicipality, bookletFY);
      await loadBooklets(bookletMunicipality, bookletFY);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to upload booklet');
    } finally {
      setUploadingBooklet(false);
    }
  };

  const handleViewBooklet = async (bookletId: string) => {
    try {
      const res = await api.getBookletUrl(bookletId);
      window.open(res.data.url, '_blank');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to get booklet URL');
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  // 'tou' -> success (teal): same hue this pill already used (bg-primary-100
  // text-primary-800, and primary is teal), so routing through Badge's
  // teal-toned 'success' tone is not a hue change, just a shade
  // normalization (800->900) — same class of adjustment BillRuns.tsx's
  // STATUS_TONE already made when it adopted Badge.
  const getRateTypeBadge = (type: string) => (
    <Badge tone={type === 'tou' ? 'success' : 'neutral'}>
      {type === 'tou' ? 'Time-of-Use' : 'Simple'}
    </Badge>
  );

  // One clock read per render, shared by every badge on the page, so no two
  // rows can straddle midnight and disagree. The rule itself never reads the
  // clock — see src/lib/tariffEffectivity.ts.
  const todayKey = todayDateKey();

  /**
   * The ONE tariff-card status pill (design change 2026-08-06) — reuses the
   * card's existing Active/Inactive slot rather than a separate badge
   * beside it. See tariffStatusPill.ts for the composed rule, the
   * precedence, and why. `versionsByTariff[tariff.id]` is `undefined`
   * before the per-tariff sweep lands or if it failed — `tariffStatusPill`
   * degrades to the plain `tariff.is_active` reading in that case, same as
   * the card showed before this module existed.
   */
  const renderStatusPill = (tariff: Tariff) => {
    const pill = tariffStatusPill(
      tariff.is_active,
      versionsByTariff[tariff.id] as TariffVersionRow[] | undefined,
      todayKey,
    );
    return <Badge tone={pill.tone}>{pill.label}</Badge>;
  };

  /**
   * The full chain-integrity span, wrappable text (not a Badge) for the
   * expanded Versions panel — the detail one click past the card badge.
   * Prefers `versionsByTariff` (already swept for the card badge, so this
   * renders the instant the panel opens) and falls back to `versions` (the
   * panel's own fetch) when the sweep has not landed yet, so the message is
   * never wrong from staleness — same data, just whichever arrived first.
   */
  const renderChainIntegrityDetail = (tariffId: string) => {
    const loaded = versionsByTariff[tariffId] ?? versions;
    const label = chainIntegrityDetailLabel(summariseChainIntegrity(loaded));
    if (label === null) return null;
    return (
      <Alert tone="danger" className="mx-6 mt-3">
        {label} Add or adjust a version to close it, or use the confirmation in
        "Add Version" / "Edit" if the gap is a deliberate work-in-progress.
      </Alert>
    );
  };

  // TV-4 form pre-submit check — recomputed every render from the CURRENT
  // form values, so the warning tracks every keystroke rather than only the
  // value the modal opened with. `versions` is this tariff's own sibling
  // list (see openVersionModal's comment on why it is always the right one
  // by the time the modal can be open). Empty `effective_from` (modal just
  // opened, nothing picked) resolves to `ok: true` — see
  // checkCandidateMesh's own guard.
  //
  // Only runs when EDITING a version whose dates ACTUALLY differ from what
  // is stored — mirroring billing-api's own `datesChanged` gate
  // (chainMeshGuard.ts wiring in index.ts). Without this, opening "Edit" on
  // either of test321's two boundary versions would show the gap warning
  // immediately, with nothing touched yet, and disable Save for a rate fix
  // or a name change — exactly the "tariff nobody can repair" failure this
  // feature exists to avoid. Both a version's OWN boundary and some other
  // version's edit must stay possible; this is what makes the first case
  // possible too, not just the already-scoped-by-id second case.
  const editedDatesUnchanged =
    editingVersion !== null &&
    chainToDateKey(versionForm.effective_from) === chainToDateKey(editingVersion.effective_from) &&
    (versionForm.effective_to || null) ===
      (editingVersion.effective_to ? chainToDateKey(editingVersion.effective_to) : null);

  const chainMesh = editedDatesUnchanged
    ? { ok: true, gaps: [], overlaps: [] }
    : checkCandidateMesh(
        editingVersion?.id ?? null,
        { effective_from: versionForm.effective_from, effective_to: versionForm.effective_to || null },
        versions,
      );

  // Financial Year identifies the version; Effective Dates is the field a
  // reader scans for next (which window this version actually applies to).
  // Status is a Badge (never primary — Badge can't wrap and DataTable's
  // collapsing branch has no overflow-x-auto fallback), so it and Updated
  // stay secondary. rowActions carries the existing "View / Edit" link.
  const versionColumns: DataTableColumn<TariffVersion>[] = [
    {
      key: 'financial_year',
      label: 'Financial Year',
      priority: 'primary',
      render: (version) => <span className="font-medium text-content">{version.financial_year}</span>,
    },
    {
      key: 'effective_dates',
      label: 'Effective Dates',
      priority: 'primary',
      render: (version) => (
        <span className="text-sm text-content-muted">
          {formatDate(version.effective_from)}
          {version.effective_to && ` - ${formatDate(version.effective_to)}`}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      priority: 'secondary',
      // Effectivity leads the cell: it is the only badge here that means
      // "you must do something", and putting it first makes the column
      // scannable for red. It stays SECONDARY — Badge is `whitespace-nowrap`,
      // so it may not sit in a primary column. Below lg that puts it in the
      // row disclosure, one tap away; discovery at that width is the
      // tariff-card badge, which is always visible.
      //
      // `flex flex-wrap gap-2` replaces the old `ml-2`: with up to three
      // nowrap pills the cell's min-content would otherwise be their sum.
      // Wrapping drops it to the widest single pill.
      render: (version) => {
        const label = versionEffectivityLabel(version, todayKey);
        return (
          <div className="flex flex-wrap items-center gap-2">
            {label !== null && (
              <Badge tone={VERSION_EFFECTIVITY_TONE[classifyVersion(version, todayKey)]}>
                {label}
              </Badge>
            )}
            {version.is_gazetted ? (
              <Badge tone="success">Gazetted</Badge>
            ) : (
              <Badge tone="warning">Provisional</Badge>
            )}
            {!version.is_active && <Badge tone="danger">Inactive</Badge>}
          </div>
        );
      },
    },
    {
      key: 'updated',
      label: 'Updated',
      priority: 'secondary',
      render: (version) => (
        <span className="text-sm text-content-muted">
          {formatDate(version.updated_at)}
          {version.updated_by && (
            <span className="block text-xs text-content-muted">by {version.updated_by}</span>
          )}
        </span>
      ),
    },
  ];

  // Filename + Financial Year identify a booklet; Uploaded (who/when) is
  // supporting detail, one tap away. rowActions carries "View PDF".
  const bookletColumns: DataTableColumn<TariffBooklet>[] = [
    {
      key: 'filename',
      label: 'Filename',
      priority: 'primary',
      // Same unbounded-free-text hazard as AuditLogs' `user` column: a
      // user-uploaded PDF filename has no guaranteed break point, so it's
      // bounded + truncated (title recovers the full name) rather than left
      // to force the row wider than the viewport.
      render: (booklet) => (
        <span className="text-sm font-medium text-content max-w-xs truncate block" title={booklet.original_filename}>
          {booklet.original_filename}
        </span>
      ),
    },
    {
      key: 'financial_year',
      label: 'Financial Year',
      priority: 'primary',
      render: (booklet) => <span className="text-sm text-content-muted">{booklet.financial_year}</span>,
    },
    {
      key: 'uploaded',
      label: 'Uploaded',
      priority: 'secondary',
      render: (booklet) => (
        <span className="text-sm text-content-muted">
          {formatDate(booklet.uploaded_at)}
          {booklet.uploaded_by && (
            <span className="block text-xs text-content-muted">by {booklet.uploaded_by}</span>
          )}
        </span>
      ),
    },
  ];

  return (
    <div className="p-4 lg:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <h1 className="text-2xl font-bold text-content">Tariff Management</h1>
        <button
          onClick={() => setShowCreateTariff(true)}
          className="px-4 py-2 bg-accent text-accent-fg rounded-lg hover:opacity-90 transition-colors flex items-center gap-2"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Create Tariff
        </button>
      </div>

      {/* Municipality Filter */}
      <Card className="shadow-sm p-4 mb-6">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-content">Municipality:</label>
          <select
            value={filterMunicipality}
            onChange={(e) => setFilterMunicipality(e.target.value)}
            className="px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">All Municipalities</option>
            {municipalities.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
        </div>
      </Card>

      {/* Admin-only: add/remove the municipalities that populate the dropdowns
          above. Hidden entirely for non-admins (also enforced server-side). */}
      <MunicipalityManager municipalities={municipalities} onChanged={loadMunicipalities} />

      {/* Error */}
      {error && (
        <Alert tone="danger" className="mb-6">
          <div className="flex items-center justify-between">
            <p>{error}</p>
            <button onClick={() => setError(null)} className="hover:opacity-70">
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </Alert>
      )}

      {/* Tariff List */}
      <div className="space-y-4">
        {loading ? (
          <div className="animate-pulse space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-border-token rounded-xl"></div>
            ))}
          </div>
        ) : tariffs.length === 0 ? (
          <Card className="shadow-sm p-12 text-center text-content-muted">
            <svg className="w-12 h-12 mx-auto mb-4 text-content-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
            <p className="text-lg font-medium">No tariffs found</p>
            <p className="mt-1">Create a new tariff to get started</p>
          </Card>
        ) : (
          tariffs.map((tariff) => (
            <Card key={tariff.id} className="shadow-sm overflow-hidden">
              {/* Tariff Header — a <div> that CONTAINS the expand control,
                  not a <button> that contains the row actions.
                  -----------------------------------------------------------
                  It used to be one big <button> wrapping the whole header,
                  with the pencil <button> and the `role="button"` Delete span
                  nested inside it. Nested interactive content is invalid HTML
                  with genuinely undefined activation behaviour — a click on
                  the pencil could fire the pencil, the header, both or
                  neither depending on engine — and in ARIA a <button>'s
                  children are presentational, so the pencil and Delete were
                  not exposed to assistive tech at all.

                  The fix keeps all three controls as SIBLINGS while changing
                  nothing about what any of them does or how the header looks:

                  - the expand control is now a bare <button> stretched over
                    the header with `absolute inset-0`, so the whole header
                    area stays one click target exactly as before. It is
                    FIRST in the DOM to preserve the old tab order
                    (header -> pencil -> Delete);
                  - an absolutely-positioned element paints above static
                    in-flow siblings, so that overlay would otherwise swallow
                    the pencil and Delete clicks. `relative z-10` on those two
                    lifts them back above it, so each keeps receiving its own
                    clicks and everything else (title, badges, description,
                    chevron) falls through to the overlay = expand;
                  - `hover:bg-surface-sunken` moves to this wrapper, so the
                    whole header still highlights on hover including over the
                    pencil, as it did when it was all one button.

                  Handlers, arguments and guards are untouched: the two row
                  actions still call handleOpenEditTariff(tariff, e) and
                  openDelete(tariff, e) with the event, and both still
                  e.stopPropagation() first. That call is now belt-and-braces
                  rather than load-bearing (a sibling has no ancestor handler
                  to stop) but it is deliberately left in place — removing it
                  would be a behaviour change for no gain. */}
              <div className="relative w-full px-6 py-4 flex items-center justify-between hover:bg-surface-sunken transition-colors">
                {/* aria-label/aria-expanded are new and necessary: stretched
                    over the header this button has no text of its own, so
                    without a label it would be an unnamed control. Its old
                    accessible name was the entire header's text. */}
                <button
                  type="button"
                  onClick={() => handleExpandTariff(tariff.id)}
                  aria-expanded={expandedTariffId === tariff.id}
                  aria-label={`${expandedTariffId === tariff.id ? 'Hide' : 'Show'} versions for ${tariff.name}`}
                  className="absolute inset-0 w-full"
                />
                {/* `min-w-0` + `flex-wrap` are what keep this header inside the
                    card at 375px, and they are two halves of ONE cause.

                    This <div> is a flex item of the header row. A flex item
                    defaults to `min-width: auto`, i.e. it may not shrink below
                    its own min-content width. Its min-content came from the row
                    below — a nowrap flex row of title + pencil + Delete + up to
                    four Badges, and Badge is `whitespace-nowrap` by design, so
                    each badge's min-content is its full pill width. Sum those
                    plus three gaps and the floor lands far past a 375px
                    viewport, so the div sat wider than the card and everything
                    past the edge was clipped by Card's `overflow-hidden` — the
                    trailing badges entirely, and the description paragraph
                    mid-word (the <p> is as wide as this div, so its text wraps
                    beyond the card edge, not at it).

                    That floor also explains the four-line title: at min-content
                    every flex item on the row is AT its min-content, and a
                    text item's min-content is its longest word. The title was
                    not being squeezed by the actions sitting beside it — title
                    and actions were both pinned to their narrowest possible
                    size while the row as a whole stayed too wide. Widening the
                    title alone would have made the overflow worse.

                    So: `min-w-0` lets the column shrink to the width actually
                    available, and `flex-wrap` drops the row's min-content from
                    "sum of all children" to "widest single child", which is one
                    badge. Both are inert at 1440px — a row that fits never wraps
                    and a floor that never binds never bites — so the desktop
                    layout the user signed off is unchanged. `gap-y-2` only ever
                    applies between wrapped lines, likewise invisible on one row.

                    `min-w-0 break-words` on the title covers the remaining case
                    the wrap can't: tariff names are free text from this page's
                    own Create/Edit form, so a single unbroken token (
                    "Ruraflex_LargePowerUser_2025_26") can exceed the line on its
                    own. `overflow-wrap` does not lower min-content, hence the
                    `min-w-0` alongside it. */}
                <div className="min-w-0 text-left">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
                    <h3 className="min-w-0 break-words font-semibold text-content">{tariff.name}</h3>
                    {/* `lg:min-h-0 lg:min-w-0 lg:block` is the ICON-sized form
                        of the documented `tap-target lg:min-h-0` escape
                        (tailwind.config.js:103). A text control is naturally
                        wider than 44px and taller than its own box, so
                        resetting min-height alone is enough for it. This is a
                        24px icon box, so all three of `.tap-target`'s effects
                        would otherwise leak into desktop: the 44px width would
                        bind and push the badges right, and `inline-flex` would
                        drop the inline line box that gives the button its
                        current 32px height, shrinking every tariff row by 8px
                        at 1440px. With all three reset, `lg`+ renders the
                        identical `p-1` block box it does today; only below
                        `lg` does the 44px floor apply. */}
                    <button
                      type="button"
                      onClick={(e) => handleOpenEditTariff(tariff, e)}
                      className="relative z-10 tap-target lg:min-h-0 lg:min-w-0 lg:block p-1 text-content-muted hover:text-accent transition-colors"
                      title="Edit tariff"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                      </svg>
                    </button>
                    {tariff.is_active === 1 && (
                      <span
                        role="button"
                        tabIndex={0}
                        onClick={(e) => openDelete(tariff, e)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openDelete(tariff, e as unknown as React.MouseEvent); }}
                        // `lg:min-h-0 lg:min-w-0` only — no `lg:block` needed
                        // here, unlike the pencil. "Delete" at text-xs is
                        // ~50px wide so the 44px min-width never binds, and
                        // its flex height (16px line box + 4px py-0.5 = 20px)
                        // equals the block height it has today, so desktop is
                        // unchanged without resetting display.
                        className="relative z-10 tap-target lg:min-h-0 lg:min-w-0 text-xs px-2 py-0.5 border border-red-200 dark:border-red-800 rounded-md text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950 cursor-pointer"
                        title="Deactivate this tariff"
                      >
                        Delete
                      </span>
                    )}
                    {getRateTypeBadge(tariff.rate_type)}
                    {/* ONE pill, reusing what used to be the bare
                        Active/Inactive slot (design change 2026-08-06,
                        tariffStatusPill.ts). It answers "can this tariff
                        bill right now?" — Active/Expires in N days/Expired/
                        Gap/Inactive, in that precedence. All 46 live
                        versions and 38 of 39 tariffs are `is_active = 1`,
                        so plain Active/Inactive had stopped carrying
                        information; this is the same slot doing a more
                        useful job, not a second badge beside it. Every tone
                        is an EXISTING Badge tone (success/warning/danger),
                        so no new colour pair. */}
                    {renderStatusPill(tariff)}
                    {tariff.category && (
                      <Badge tone="info">{tariff.category}</Badge>
                    )}
                    {tariff.latest_fy && (
                      <Badge tone="purple">FY {tariff.latest_fy}</Badge>
                    )}
                  </div>
                  <p className="text-sm text-content-muted mt-1">
                    {tariff.description || 'No description'}
                  </p>
                  <p className="text-xs text-content-muted mt-1">
                    {tariff.municipality_name && `${tariff.municipality_name} | `}
                    {tariff.version_count ?? 0} version{tariff.version_count !== 1 ? 's' : ''}
                  </p>
                </div>
                {/* THE COLLAPSE BUG (user: "once clicked on expand, you can't
                    collapse them again"). This was a bare <svg> sibling of the
                    stretched overlay, relying on the fall-through the comment
                    above describes. That fall-through held while COLLAPSED and
                    silently inverted while EXPANDED:

                      `rotate-180` is not a bare rotate — Tailwind emits
                      `transform: translate(..) rotate(var(--tw-rotate)) skew..`
                      (confirmed against the compiled CSS). Any transform other
                      than `none` creates a stacking context
                      (css-transforms-1 §3), and such an element paints in
                      CSS 2.1 Appendix E step 8 — the SAME step as a positioned
                      `z-index: auto` element, ordered by TREE ORDER.

                    The overlay is `absolute` (step 8) and comes FIRST in the
                    DOM; this chevron comes last. So:
                      collapsed → no transform → static in-flow (step 3/7),
                                  paints BELOW the overlay, clicks fall
                                  through → expand fires. Worked.
                      expanded  → transform → step 8, after the overlay in tree
                                  order → paints ABOVE it. The click landed on
                                  an <svg> with no handler → nothing happened.

                    Hence expand worked and collapse did not, on the one element
                    users actually aim at. jsdom clicks elements directly and
                    implements no hit-testing, so no jsdom test could see it.

                    Fix is structural, not a paint-order patch: the chevron is
                    now a real control that is `relative z-10` in BOTH states,
                    so it no longer depends on the transform question at all.
                    It is a sibling of the overlay, not a descendant, so a click
                    on it never reaches the overlay — exactly one toggle fires
                    per click (the same sibling argument the pencil comment
                    makes above). `aria-hidden` + `tabIndex={-1}` keep the
                    labelled overlay as the SINGLE control exposed to assistive
                    tech and the single tab stop, so the card does not announce
                    two identical toggles.

                    `shrink-0` moves to the button (it is the flex item now);
                    the text column may shrink, this must not. `.tap-target`
                    gives the 44px touch floor below `lg` and
                    `lg:min-h-0 lg:min-w-0` resets it, so at 1440px the button
                    is exactly the 20px svg box it renders today — Tailwind's
                    preflight zeroes button padding/border/background, and a
                    flex item's display is blockified either way, so desktop
                    geometry is unchanged. */}
                <button
                  type="button"
                  onClick={() => handleExpandTariff(tariff.id)}
                  aria-hidden="true"
                  tabIndex={-1}
                  className="relative z-10 shrink-0 tap-target lg:min-h-0 lg:min-w-0"
                >
                  <svg
                    className={`w-5 h-5 text-content-muted transition-transform ${
                      expandedTariffId === tariff.id ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              {/* Expanded Version List */}
              {expandedTariffId === tariff.id && (
                <div className="border-t border-border-token">
                  {renderChainIntegrityDetail(tariff.id)}
                  <div className="px-6 py-3 bg-surface-sunken flex items-center justify-between">
                    <h4 className="text-sm font-medium text-content">Versions</h4>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openVersionModal(tariff.id);
                      }}
                      className="px-3 py-1.5 text-sm bg-accent text-accent-fg rounded-lg hover:opacity-90 transition-colors flex items-center gap-1"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                      </svg>
                      Add Version
                    </button>
                  </div>

                  {loadingVersions ? (
                    <div className="p-6">
                      <div className="animate-pulse space-y-2">
                        {[1, 2].map((i) => (
                          <div key={i} className="h-12 bg-border-token rounded"></div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <DataTable
                      data={versions}
                      columns={versionColumns}
                      rowKey={(version) => version.id}
                      emptyState={<p>No versions yet. Add a version with rates to activate this tariff.</p>}
                      /* `lg:min-h-0`: 44px is a TOUCH floor; in a table row it
                         drives row height and costs rows-per-screen on
                         desktop. Full rationale on Tenants.tsx's rowActions;
                         escape documented at tailwind.config.js:103. */
                      rowActions={(version) => (
                        <button
                          onClick={() => openVersionModal(tariff.id, version)}
                          className="tap-target lg:min-h-0 text-accent hover:opacity-90 text-sm font-medium"
                        >
                          View / Edit
                        </button>
                      )}
                    />
                  )}
                </div>
              )}
            </Card>
          ))
        )}
      </div>

      {/* Booklet Section */}
      <div className="mt-8">
        <Card className="shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-border-token">
            <h2 className="text-lg font-semibold text-content">Tariff Booklets</h2>
            <p className="text-sm text-content-muted mt-1">Upload and manage gazetted tariff booklet PDFs by municipality and financial year.</p>
          </div>

          <div className="p-6">
            <div className="flex items-end gap-4 mb-6">
              <div>
                <label className="block text-sm font-medium text-content mb-1">Municipality</label>
                <select
                  value={bookletMunicipality}
                  onChange={(e) => {
                    setBookletMunicipality(e.target.value);
                    if (e.target.value && bookletFY) loadBooklets(e.target.value, bookletFY);
                  }}
                  className="px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">Select municipality</option>
                  {municipalities.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-content mb-1">Financial Year</label>
                <input
                  type="text"
                  placeholder="e.g. 2025/26"
                  value={bookletFY}
                  onChange={(e) => {
                    setBookletFY(e.target.value);
                    if (bookletMunicipality && e.target.value) loadBooklets(bookletMunicipality, e.target.value);
                  }}
                  className="px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent w-32"
                />
              </div>
              <div>
                <label className="relative px-4 py-2 bg-accent text-accent-fg rounded-lg hover:opacity-90 transition-colors cursor-pointer inline-flex items-center gap-2">
                  {uploadingBooklet ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Uploading...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                      </svg>
                      Upload Booklet
                    </>
                  )}
                  <input
                    type="file"
                    accept=".pdf"
                    className="absolute inset-0 opacity-0 cursor-pointer"
                    disabled={!bookletMunicipality || !bookletFY || uploadingBooklet}
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleUploadBooklet(file);
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            </div>

            {/* Booklet list */}
            {loadingBooklets ? (
              <div className="animate-pulse space-y-2">
                {[1, 2].map((i) => (
                  <div key={i} className="h-10 bg-border-token rounded"></div>
                ))}
              </div>
            ) : booklets.length === 0 ? (
              <div className="text-center text-content-muted py-6">
                {bookletMunicipality && bookletFY
                  ? 'No booklets uploaded for this municipality and financial year.'
                  : 'Select a municipality and financial year to view booklets.'}
              </div>
            ) : (
              <DataTable
                data={booklets}
                columns={bookletColumns}
                rowKey={(booklet) => booklet.id}
                /* `lg:min-h-0` — see the versions table above. */
                rowActions={(booklet) => (
                  <button
                    onClick={() => handleViewBooklet(booklet.id)}
                    className="tap-target lg:min-h-0 text-accent hover:opacity-90 text-sm font-medium"
                  >
                    View PDF
                  </button>
                )}
              />
            )}
          </div>
        </Card>
      </div>

      {/* Create Tariff Modal */}
      {showCreateTariff && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-surface-raised rounded-xl shadow-2xl w-full max-w-md p-6">
            <h2 className="text-xl font-bold text-content mb-6">Create Tariff</h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-content mb-1">Municipality</label>
                <select
                  value={newTariff.municipality_id}
                  onChange={(e) => setNewTariff({ ...newTariff, municipality_id: e.target.value })}
                  className="w-full px-4 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="">None (global)</option>
                  {municipalities.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-content mb-1">Name</label>
                <input
                  type="text"
                  value={newTariff.name}
                  onChange={(e) => setNewTariff({ ...newTariff, name: e.target.value })}
                  placeholder="e.g. Residential Standard"
                  className="w-full px-4 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-content mb-1">Description</label>
                <textarea
                  value={newTariff.description}
                  onChange={(e) => setNewTariff({ ...newTariff, description: e.target.value })}
                  rows={3}
                  placeholder="Optional description"
                  className="w-full px-4 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-content mb-1">Category</label>
                <input
                  type="text"
                  value={newTariff.category}
                  onChange={(e) => setNewTariff({ ...newTariff, category: e.target.value })}
                  placeholder="e.g. residential, commercial"
                  className="w-full px-4 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-content mb-1">Rate Type</label>
                <select
                  value={newTariff.rate_type}
                  onChange={(e) => setNewTariff({ ...newTariff, rate_type: e.target.value as 'simple' | 'tou' })}
                  className="w-full px-4 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="simple">Simple</option>
                  <option value="tou">Time-of-Use</option>
                </select>
              </div>
              <label className="flex items-start gap-2 text-sm text-content">
                <input
                  type="checkbox"
                  checked={newTariff.restriction_exempt}
                  onChange={(e) => setNewTariff({ ...newTariff, restriction_exempt: e.target.checked })}
                  className="mt-0.5"
                />
                <span>
                  Not affected by water restrictions
                  <span className="block text-xs text-content-muted">
                    Water and sanitation per kL stay at Water Wise rates whatever the municipality's restriction level.
                  </span>
                </span>
              </label>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateTariff(false);
                  setError(null);
                }}
                className="px-4 py-2 text-content bg-surface-sunken rounded-lg hover:bg-border-token transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTariff}
                disabled={creating}
                className="px-4 py-2 bg-accent text-accent-fg rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {creating && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                Create Tariff
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Version Modal */}
      {showVersionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 overflow-y-auto py-8">
          <div className="bg-surface-raised rounded-xl shadow-2xl w-full max-w-3xl p-6">
            <h2 className="text-xl font-bold text-content mb-6">
              {editingVersion ? 'Edit Version' : 'Add Version'}
            </h2>

            <div className="space-y-4">
              {/* Version meta fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Financial Year</label>
                  <input
                    type="text"
                    value={versionForm.financial_year}
                    onChange={(e) => setVersionForm({ ...versionForm, financial_year: e.target.value })}
                    placeholder="e.g. 2025/26"
                    disabled={!!editingVersion}
                    className="w-full px-4 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:bg-surface-sunken"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Effective From</label>
                  <input
                    type="date"
                    value={versionForm.effective_from}
                    onChange={(e) => {
                      // Reset acknowledgeGap: an acknowledgment given for one
                      // gap must never silently carry over and rescue a
                      // DIFFERENT gap the next date change opens up.
                      setAcknowledgeGap(false);
                      setVersionForm({ ...versionForm, effective_from: e.target.value });
                    }}
                    className="w-full px-4 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Effective To (optional)</label>
                  <input
                    type="date"
                    value={versionForm.effective_to}
                    onChange={(e) => {
                      setAcknowledgeGap(false);
                      setVersionForm({ ...versionForm, effective_to: e.target.value });
                    }}
                    className="w-full px-4 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-end gap-4">
                  <label className="flex items-center gap-2 cursor-pointer pb-2">
                    <input
                      type="checkbox"
                      checked={versionForm.is_gazetted}
                      onChange={(e) => setVersionForm({ ...versionForm, is_gazetted: e.target.checked })}
                      className="w-4 h-4 text-accent border-border-token rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-content">Gazetted</span>
                  </label>
                </div>
              </div>

              {/* TV-4 pre-submit chain-mesh warning — surfaced BEFORE
                  submit, so the server's 409 (chainMeshGuard.ts) is never
                  the first the operator hears of it. Overlap has no escape
                  hatch (Save stays disabled); a gap does, gated on an
                  explicit checkbox that names the exact span so "acknowledge
                  it" is an informed choice, not a shrug. */}
              {chainMesh.overlaps.length > 0 && (
                <Alert tone="danger">
                  This overlaps existing version {chainMesh.overlaps[0].bVersionId} from{' '}
                  {chainMesh.overlaps[0].start}
                  {chainMesh.overlaps[0].end ? ` to ${chainMesh.overlaps[0].end}` : ' onward'}. Two versions
                  cannot both price the same day — narrow one of the date ranges.
                </Alert>
              )}
              {chainMesh.overlaps.length === 0 && chainMesh.gaps.length > 0 && (
                <Alert tone="warning">
                  <p>
                    This leaves {chainMesh.gaps[0].start} to {chainMesh.gaps[0].end} with no tariff
                    version.
                  </p>
                  <label className="flex items-center gap-2 mt-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={acknowledgeGap}
                      onChange={(e) => setAcknowledgeGap(e.target.checked)}
                      className="w-4 h-4 text-accent border-border-token rounded focus:ring-primary-500"
                    />
                    <span className="text-sm">
                      Save anyway — the gap will be flagged on this tariff until it is fixed
                    </span>
                  </label>
                </Alert>
              )}

              {versionForm.is_gazetted && (
                <div className="max-w-xs">
                  <label className="block text-sm font-medium text-content mb-1">Gazetted Date</label>
                  <input
                    type="date"
                    value={versionForm.gazetted_date}
                    onChange={(e) => setVersionForm({ ...versionForm, gazetted_date: e.target.value })}
                    className="w-full px-4 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>
              )}

              {/* Rates Table */}
              <div className="border-t border-border-token pt-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-content">Rates</h3>
                  <button
                    onClick={addRateRow}
                    className="px-3 py-1 text-sm bg-surface-sunken text-content rounded-lg hover:bg-border-token transition-colors flex items-center gap-1"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Add Row
                  </button>
                </div>

                {/* Rate-rows editor: 7 columns (Charge Type, TOU Bucket,
                    Season, Rate, Unit, Description, delete) — the review's
                    count of 8 was one high. Well past the >=6 threshold, and
                    the worst case of the set: five of the seven cells hold a
                    form control (<select>/<input>), which have a hard
                    min-content width and degrade to unusable rather than
                    merely cramped when squeezed. `min-w-max` so the wrapper
                    scrolls instead. See DataTable.tsx:104-121. */}
                <div className="overflow-x-auto">
                  <table className="w-full min-w-max text-sm">
                    <thead className="bg-surface-sunken">
                      <tr>
                        <th className="px-2 py-2 text-left text-xs font-medium text-content-muted uppercase">Charge Type</th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-content-muted uppercase">TOU Bucket</th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-content-muted uppercase">Season</th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-content-muted uppercase">Rate (R)</th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-content-muted uppercase">Unit</th>
                        <th className="px-2 py-2 text-left text-xs font-medium text-content-muted uppercase">Description</th>
                        <th className="px-2 py-2 w-10"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-token">
                      {rateRows.map((row, idx) => (
                        <tr key={idx}>
                          <td className="px-2 py-1.5">
                            <select
                              value={row.charge_type}
                              onChange={(e) => updateRateRow(idx, 'charge_type', e.target.value)}
                              className="w-full px-2 py-1.5 border border-border-token rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            >
                              {CHARGE_TYPE_OPTIONS.map((ct) => (
                                <option key={ct} value={ct}>{chargeTypeLabel(ct)}</option>
                              ))}
                            </select>
                            {/* The level qualifies the type, so it lives under it, like the
                                sanitation % under its rate: as a column it would sit past the
                                table's scroll edge (spec 2026-10-08). */}
                            {isRestrictableChargeType(row.charge_type) && (
                              <select
                                aria-label="Restriction level"
                                value={row.restriction_level ?? ''}
                                onChange={(e) => updateRateRow(idx, 'restriction_level', e.target.value === '' ? null : e.target.value)}
                                className="mt-1 w-full px-2 py-1.5 border border-border-token rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                              >
                                <option value="">{WATER_WISE_LABEL}</option>
                                {RESTRICTION_LEVELS.map((l) => (
                                  <option key={l} value={l}>{LEVEL_LABELS[l]}</option>
                                ))}
                              </select>
                            )}
                          </td>
                          <td className="px-2 py-1.5">
                            <select
                              value={row.tou_bucket}
                              onChange={(e) => updateRateRow(idx, 'tou_bucket', e.target.value)}
                              className="w-full px-2 py-1.5 border border-border-token rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            >
                              {TOU_BUCKET_OPTIONS.map((b) => (
                                <option key={b} value={b}>{b.replace('_', ' ')}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1.5">
                            <select
                              value={row.season}
                              onChange={(e) => updateRateRow(idx, 'season', e.target.value)}
                              className="w-full px-2 py-1.5 border border-border-token rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            >
                              {SEASON_OPTIONS.map((s) => (
                                <option key={s} value={s}>{s}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1.5">
                            <div className="relative">
                              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-content-muted text-sm pointer-events-none">R</span>
                              <input
                                type="number"
                                value={(row.rate_cents / 100).toString()}
                                onChange={(e) => {
                                  const rands = Number(e.target.value);
                                  // Store in cents at 2-decimal precision (4 decimals of Rand)
                                  updateRateRow(idx, 'rate_cents', Math.round(rands * 10000) / 100);
                                }}
                                className="w-full pl-6 pr-2 py-1.5 border border-border-token rounded text-sm font-mono focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                                min={0}
                                step={0.0001}
                                placeholder="0.0000"
                              />
                            </div>
                            {/* The % lives under the rate it modifies: as its own
                                column it sat past the table's scroll edge. */}
                            {row.charge_type === 'sanitation_kl' && (
                              <div className="mt-1 flex items-center gap-1.5">
                                <input
                                  type="number"
                                  aria-label="% of water consumption"
                                  aria-invalid={showPercentErrors && !isValidVolumePercent(row.volume_percent)}
                                  value={row.volume_percent ?? ''}
                                  onChange={(e) => updateRateRow(idx, 'volume_percent', e.target.value === '' ? null : Number(e.target.value))}
                                  className={`w-20 px-2 py-1.5 border rounded text-sm font-mono focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                                    showPercentErrors && !isValidVolumePercent(row.volume_percent) ? 'border-red-500' : 'border-border-token'
                                  }`}
                                  min={0}
                                  max={100}
                                  step={0.5}
                                  placeholder="95"
                                />
                                <span className="text-xs text-content-muted whitespace-nowrap">% of water</span>
                              </div>
                            )}
                          </td>
                          <td className="px-2 py-1.5">
                            <select
                              value={row.unit}
                              onChange={(e) => updateRateRow(idx, 'unit', e.target.value)}
                              className="w-full px-2 py-1.5 border border-border-token rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            >
                              {UNIT_OPTIONS.map((u) => (
                                <option key={u} value={u}>{u}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1.5">
                            <input
                              type="text"
                              value={row.description}
                              onChange={(e) => updateRateRow(idx, 'description', e.target.value)}
                              placeholder="Optional"
                              className="w-full px-2 py-1.5 border border-border-token rounded text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                            />
                          </td>
                          <td className="px-2 py-1.5">
                            <button
                              onClick={() => removeRateRow(idx)}
                              disabled={rateRows.length <= 1}
                              className="text-red-500 hover:text-red-700 disabled:opacity-30 disabled:cursor-not-allowed"
                              title="Remove row"
                            >
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {error && (
              <Alert tone="danger" className="mt-4">
                {error}
              </Alert>
            )}

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowVersionModal(false);
                  setEditingVersion(null);
                  setEditingTariffId(null);
                  setError(null);
                }}
                className="px-4 py-2 text-content bg-surface-sunken rounded-lg hover:bg-border-token transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveVersion}
                disabled={
                  savingVersion ||
                  chainMesh.overlaps.length > 0 ||
                  (chainMesh.gaps.length > 0 && !acknowledgeGap)
                }
                className="px-4 py-2 bg-accent text-accent-fg rounded-lg hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {savingVersion && (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                )}
                {editingVersion ? 'Update Version' : 'Create Version'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Edit Tariff Modal */}
      {showEditTariff && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={closeEditTariff} />
          {/* max-h-[90vh] overflow-y-auto: with the Municipality select and
              the holiday-pricing warning this panel is taller than a 667px
              screen, and the centring overlay above is fixed and cannot
              scroll, so the panel scrolls itself (MeterConfigModal /
              ReportSchedulePanel pattern). At 1440px 90vh is taller than the
              panel, so desktop is unchanged. */}
          <div className="relative bg-surface-raised rounded-xl shadow-xl w-full max-w-md mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-lg font-bold text-content mb-4">Edit Tariff</h2>
            <form onSubmit={handleUpdateTariff} className="space-y-4">
              <div>
                <label htmlFor="edit-tariff-municipality" className="block text-sm font-medium text-content mb-1">Municipality</label>
                {/* Same options as the Create form. A native select is fine
                    bare per the tap-target rule (it centres its own value). */}
                <select
                  id="edit-tariff-municipality"
                  value={editTariffForm.municipality_id}
                  onChange={(e) => {
                    setEditTariffForm((f) => ({ ...f, municipality_id: e.target.value }));
                    // A warning was about the previous target; never let
                    // "Change anyway" acknowledge a different one.
                    setHolidayPricingWarning(null);
                  }}
                  className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                >
                  <option value="">None (global)</option>
                  {editTariffOriginalMunicipality.id !== ''
                    && !municipalities.some((m) => m.id === editTariffOriginalMunicipality.id) && (
                      // The list failed to load or no longer has it: keep the
                      // current value visible rather than showing "None".
                      <option value={editTariffOriginalMunicipality.id}>
                        {editTariffOriginalMunicipality.name ?? editTariffOriginalMunicipality.id}
                      </option>
                  )}
                  {municipalities.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-content mb-1">Name *</label>
                <input
                  type="text"
                  required
                  value={editTariffForm.name}
                  onChange={(e) => setEditTariffForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-content mb-1">Description</label>
                <textarea
                  value={editTariffForm.description}
                  onChange={(e) => setEditTariffForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none resize-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-content mb-1">Category</label>
                <input
                  type="text"
                  value={editTariffForm.category}
                  onChange={(e) => setEditTariffForm((f) => ({ ...f, category: e.target.value }))}
                  className="w-full px-3 py-2 border border-border-token rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  placeholder="e.g. commercial, residential"
                />
              </div>
              <label className="flex items-start gap-2 text-sm text-content">
                <input
                  type="checkbox"
                  checked={editTariffForm.restriction_exempt}
                  onChange={(e) => setEditTariffForm((f) => ({ ...f, restriction_exempt: e.target.checked }))}
                  className="mt-0.5"
                />
                <span>
                  Not affected by water restrictions
                  <span className="block text-xs text-content-muted">
                    Water and sanitation per kL stay at Water Wise rates whatever the municipality's restriction level.
                  </span>
                </span>
              </label>
              {holidayPricingWarning && (
                <Alert tone="warning">
                  <p>{holidayPricingWarning}</p>
                  <button
                    type="button"
                    onClick={() => submitTariffUpdate(true)}
                    disabled={updatingTariff}
                    className="mt-3 px-4 py-2 bg-accent text-accent-fg rounded-lg hover:opacity-90 disabled:opacity-50 font-medium"
                  >
                    Change anyway
                  </button>
                </Alert>
              )}
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={closeEditTariff} className="px-4 py-2 text-content bg-surface-sunken rounded-lg hover:bg-border-token font-medium">
                  Cancel
                </button>
                <button type="submit" disabled={updatingTariff} className="px-4 py-2 bg-accent text-accent-fg rounded-lg hover:opacity-90 disabled:opacity-50 font-medium">
                  {updatingTariff ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Type-to-confirm delete modal. The destructive button stays disabled
          until the typed text matches the tariff name exactly so similarly
          named tariffs ("Landrate 1" vs "Landrate 2") can't be confused. */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-surface-raised rounded-xl shadow-2xl w-full max-w-md">
            <div className="px-6 py-4 border-b border-border-token">
              <h3 className="text-lg font-semibold text-content">Deactivate tariff</h3>
            </div>
            <div className="px-6 py-4 space-y-3 text-sm">
              <p className="text-content">
                You're about to deactivate{' '}
                <span className="font-semibold text-content">{deleteTarget.name}</span>.
                It will be hidden from picker dropdowns. Meters still assigned will keep
                the link until you re-assign them.
              </p>
              <p className="text-content-muted">
                To confirm, type the tariff name exactly:
                <span className="ml-1 font-mono text-content">{deleteTarget.name}</span>
              </p>
              <input
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="Type tariff name here"
                autoFocus
                className="w-full px-3 py-2 border border-border-token rounded-md font-mono text-sm focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <div className="px-6 py-4 border-t border-border-token flex justify-end gap-2">
              <button
                onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }}
                disabled={deleting}
                className="px-3 py-1.5 text-sm border border-border-token rounded-md hover:bg-surface-sunken disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                disabled={deleting || deleteConfirmText.trim() !== deleteTarget.name}
                className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-md hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {deleting ? 'Deactivating…' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
